import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import SettlementButton from "@/features/my-order/features/settlement-management/components/SettlementButton";
import useGetCreditInvoiceList from "@/features/my-order/features/settlement-management/hooks/query/useGetCreditInvoiceList";
import CenteredSpinner from "@/shared/components/centered-spinner";
import { useRecoilValue } from "recoil";
import settlementManagementCreditLoadingScope from "@/features/my-order/features/settlement-management/stores/useSettlementManagementCreditLoadingScope";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { formatDateToYMDGeneral } from "@/shared/utils";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import CustomizedTooltip from "@/shared/components/CustomizedTooltip";
import { DenseCreditInvoice } from "@/features/invoices/credit-invoices/credit-invoice.type";
import { GetCreditInvoiceListRequest } from "@/features/my-order/features/settlement-management/types";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import NoResultSimple from "@/shared/components/no-result-simple";

interface SettlementManagementCreditListProps {
  params?: GetCreditInvoiceListRequest;
  isEnabled?: boolean;
}

function SettlementManagementCreditList({
  params,
  isEnabled = true,
}: SettlementManagementCreditListProps) {
  const { data, isFetching } = useGetCreditInvoiceList(params, {
    enabled: isEnabled,
  });
  const { tSentence } = useI18nHelper();
  // const loadingScope = useRecoilValue(settlementManagementCreditLoadingScope);

  // if (isFetching && loadingScope === "LIST") return <CenteredSpinner />;

  return (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      overflow={"auto"}
      padding={"16px"}
      paddingTop={"1px"}
      flex={1}
    >
      {data?.creditInvoices.length === 0 && (
        <NoResultSimple
          size={"48px"}
          text={tSentence(
            `settlement:credit_${
              params?.isUsed__eq ? "used" : "available"
            }_no_result_description`
          )}
          textStyle={"body-lg-regular"}
          gap={"16px"}
        />
      )}
      {data?.creditInvoices.map((credit) => (
        <CreditCardItem key={credit.id} credit={credit} />
      ))}
    </Flex>
  );
}

export default SettlementManagementCreditList;

interface CreditCardItemProps {
  credit: DenseCreditInvoice;
}

const CreditCardItem = ({ credit }: CreditCardItemProps) => {
  const { tTitle, tCurrencyString } = useI18nHelper();
  const { navigateToLineSheetPage } = useRouterHelper();

  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();

  const isUsed = credit.isUsed;

  return (
    <Flex
      key={credit.id}
      gap={"12px"}
      padding={"16px"}
      borderRadius={"8px"}
      border={isUsed ? "none" : "1px solid #D4D4D1"}
      boxShadow={
        isUsed ? "0 0 0 0.5px #D4D4D1" : "0 2px 4px 0 rgba(0, 0, 0, 0.10)"
      }
    >
      <Box
        padding={"11px 9px 9px 11px"}
        borderRadius={"8px"}
        background={isUsed ? "#EFEFED" : "#FFF7CC"}
        alignSelf={"flex-start"}
      >
        <CustomIcon
          src={"coin"}
          width={28}
          height={28}
          color={isUsed ? "#C0C0C0" : "#B27700"}
        />
      </Box>
      <Flex flexDirection={"column"} gap={"4px"} flex={1}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Text
            textStyle={"body-lg-bold"}
            color={isUsed ? "#C0C0C0" : "#444444"}
          >
            {tCurrencyString(credit.amount.value)}
          </Text>
          <Flex gap={"12px"} alignItems={"center"}>
            {credit.file?.id && (
              <Flex
                gap={"4px"}
                alignItems={"center"}
                className={"group"}
                cursor={"pointer"}
                _hover={{ color: "#1272EF" }}
                _active={{ color: "#00417D" }}
                onClick={() => {
                  if (!credit.file?.id) return;
                  fireDownloadUnoFile({ unoFileId: credit.file.id }, {});
                }}
              >
                <Box
                  color={"#8F8F8C"}
                  _groupHover={{ color: "inherit" }}
                  _groupActive={{ color: "inherit" }}
                >
                  <CustomIcon src={"document-down"} width={16} height={16} />
                </Box>
                <Text
                  textStyle={"body-sm-bold"}
                  color={"#444444"}
                  _groupHover={{ color: "inherit" }}
                  _groupActive={{ color: "inherit" }}
                >
                  {tTitle("order_history:documents")}
                </Text>
              </Flex>
            )}
            {/*{!isUsed && (*/}
            {/*  <SettlementButton*/}
            {/*    iconSpacing={"4px"}*/}
            {/*    color={"#1272EF"}*/}
            {/*    onClick={() =>*/}
            {/*      navigateToLineSheetPage(*/}
            {/*        undefined,*/}
            {/*        undefined,*/}
            {/*        credit.issuedByCompany.id*/}
            {/*      )*/}
            {/*    }*/}
            {/*    rightIcon={*/}
            {/*      <CustomIcon src={"chevron-right"} width={16} height={16} />*/}
            {/*    }*/}
            {/*  >*/}
            {/*    {tTitle("use_now")}*/}
            {/*  </SettlementButton>*/}
            {/*)}*/}
          </Flex>
        </Flex>
        <Flex gap={"8px"} alignItems={"center"}>
          <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
            {tTitle("issued_present")}
          </Text>
          {/*<CustomizedTooltip*/}
          {/*  label={*/}
          {/*    isLinkable*/}
          {/*      ? tTitle("settlement:fee_tooltip.open_order_with_new_tab")*/}
          {/*      : ""*/}
          {/*  }*/}
          {/*  hasArrow={true}*/}
          {/*>*/}
          <Flex
            gap={"6px"}
            alignItems={"center"}
            className={"group"}
            // cursor={"pointer"}
            // _hover={{
            //   textDecoration: "underline",
            // }}
          >
            <Text textStyle={"body-sm-medium"} color={"#444444"}>
              {credit.season}{" "}
              {tTitle(
                `settlement:${
                  credit.type === "SEASON"
                    ? "deposit_conversion"
                    : "claim_compensation"
                }`
              )}{" "}
              (
              {formatDateToYMDGeneral({
                date: credit.issuedOn,
                joinTxt: "-",
                isKor: true,
              })}{" "}
              {tTitle("issued_present")})
            </Text>
            {/*<Box*/}
            {/*  color={"#8F8F8C"}*/}
            {/*  _groupHover={{*/}
            {/*    color: "#1272EF",*/}
            {/*  }}*/}
            {/*  _groupActive={{*/}
            {/*    color: "#00417D",*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <CustomIcon src={"external-link"} width={16} height={16} />*/}
            {/*</Box>*/}
          </Flex>
          {/*</CustomizedTooltip>*/}
        </Flex>
        {isUsed && (
          <Flex gap={"8px"} alignItems={"center"}>
            <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
              {tTitle("used_past")}
            </Text>
            {/*<CustomizedTooltip*/}
            {/*  label={*/}
            {/*    isLinkable*/}
            {/*      ? tTitle("settlement:fee_tooltip.open_order_with_new_tab")*/}
            {/*      : ""*/}
            {/*  }*/}
            {/*  hasArrow={true}*/}
            {/*>*/}
            {/*  <Flex*/}
            {/*    gap={"6px"}*/}
            {/*    alignItems={"center"}*/}
            {/*    cursor={"pointer"}*/}
            {/*    className={"group"}*/}
            {/*  >*/}
            {/*    <Text textStyle={"body-sm-medium"} color={"#444444"}>*/}
            {/*      상품대금 사용 (*/}
            {/*      {formatDateToYMDGeneral({*/}
            {/*        date: credit.issuedOn,*/}
            {/*        joinTxt: "-",*/}
            {/*        isKor: true,*/}
            {/*      })}{" "}*/}
            {/*      {tTitle("used_present")})*/}
            {/*    </Text>*/}
            {/*    <Box*/}
            {/*      color={"#8F8F8C"}*/}
            {/*      _groupHover={{*/}
            {/*        color: "#1272EF",*/}
            {/*      }}*/}
            {/*      _groupActive={{*/}
            {/*        color: "#00417D",*/}
            {/*      }}*/}
            {/*    >*/}
            {/*      <CustomIcon src={"external-link"} width={16} height={16} />*/}
            {/*    </Box>*/}
            {/*  </Flex>*/}
            {/*</CustomizedTooltip>*/}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
